import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import PhoneInput from "react-phone-number-input";
import * as yup from "yup";
import { toast } from "react-toastify";
import CheckCircleIcon from "@iconscout/react-unicons/icons/uil-check-circle";

/* IMAGE IMPORTS */
import AmiKnox from "../../assets/agents/ami-knox.webp";
import MichealAvendano from "../../assets/agents/michael-avendano.webp";
import JamesSmith from "../../assets/agents/benny-chavez.webp";

/* API IMPORTS */
import { contactUs } from "../../service/api";

/* STYLING IMPORTS */
import "react-phone-number-input/style.css";

const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  phone: yup.string().required("Phone Number is required"),
  message: yup.string().required("Message is required"),
});

const ContactUs = () => {
  /* UPDATE META DATA */
  const metaTitle = "Contact Us | Amberwood Real Estate";
  const metaDescription =
    "Get in touch with Amberwood Real Estate. Visit our office, meet the team, or send us a message and we will get back to you shortly.";
  const metaImage =
    "https://storage.googleapis.com/cdn-amberwoodre/images/amberwood-og-image.png";

  document.title = metaTitle;
  document
    .querySelector('meta[name="description"]')
    .setAttribute("content", metaDescription);

  document
    .querySelector('meta[property="og:title"]')
    .setAttribute("content", metaTitle);
  document
    .querySelector('meta[property="og:description"]')
    .setAttribute("content", metaDescription);
  document
    .querySelector('meta[property="og:image"]')
    .setAttribute("content", metaImage);
  document
    .querySelector('meta[property="og:url"]')
    .setAttribute("content", window.location.href);

  document
    .querySelector('meta[name="twitter:title"]')
    .setAttribute("content", metaTitle);
  document
    .querySelector('meta[name="twitter:description"]')
    .setAttribute("content", metaDescription);
  document
    .querySelector('meta[name="twitter:image"]')
    .setAttribute("content", metaImage);

  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      type: "CONTACTUS",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      contactUs(values)
        .then((res) => {
          if (res?.status) {
            resetForm();
            toast.success(res?.message);
            setShowSuccess(true);
          }
        })
        .catch((e) => {
          if (e?.response?.data?.message) {
            toast.error(e?.response?.data?.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <section className="our-team contact-us">
      {/* BANNER */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="page-banner"
      >
        <div className="banner-content">
          <div className="container">
            <div className="row align-items-center justify-content-between">
              <div className="col-xl-6 col-lg-6 col-sm-12">
                <div className="banner-text">
                  <span className="eyebrow">CONTACT US</span>
                  <h1>Let’s connect and explore how we can help you</h1>
                  <p>
                    Visit our office, meet the team, or send a message and we
                    will get back to you shortly.
                  </p>
                  <div className="contact-highlights">
                    <div className="highlight">
                      <p className="label">Office Address</p>
                      <p className="value">
                        11060 Artesia Blvd Suite G, Cerritos, CA 90703
                      </p>
                    </div>
                    <div className="highlight">
                      <p className="label">Office Hours</p>
                      <p className="value">Mon - Fri: 9:00 AM - 6:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-5 col-lg-6 col-sm-12 ms-auto">
                <div className="form-section contact-form-card">
                  {showSuccess ? (
                    <div className="success-state">
                      <div className="success-icon">
                        <CheckCircleIcon size="48" color="#0E7A45" />
                      </div>
                      <h3>Thank you! We received your message.</h3>
                      <p>
                        Our team will reach out shortly. If you need immediate
                        assistance, call us directly.
                      </p>
                      <Button
                        className="primary-btn w-100"
                        type="button"
                        onClick={() => setShowSuccess(false)}
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={formik.handleSubmit}>
                      <h3>GET IN TOUCH</h3>
                      <div className="row">
                        <div className="col-xl-12 col-sm-12">
                          <div className="form-group">
                            <label htmlFor="name" className="form-label">
                              Name <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                formik.errors.name && formik.touched.name
                                  ? "validation-border"
                                  : ""
                              }`}
                              name="name"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              placeholder="John Doe"
                            />
                          </div>
                        </div>
                        <div className="col-xl-12 col-sm-12">
                          <div className="form-group">
                            <label htmlFor="email" className="form-label">
                              Email Address{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="email"
                              className={`form-control ${
                                formik.errors.email && formik.touched.email
                                  ? "validation-border"
                                  : ""
                              }`}
                              name="email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              placeholder="Email Address"
                            />
                          </div>
                        </div>
                        <div className="col-xl-12 col-sm-12">
                          <div className="form-group">
                            <label htmlFor="phone" className="form-label">
                              Phone <span style={{ color: "red" }}>*</span>
                            </label>
                            <PhoneInput
                              placeholder="Phone Number"
                              type="text"
                              defaultCountry="US"
                              name="phone"
                              value={formik.values.phone}
                              onChange={(phone) =>
                                formik.setFieldValue("phone", phone)
                              }
                              className={`phone-number-select ${
                                formik.errors.phone && formik.touched.phone
                                  ? "validation-border"
                                  : ""
                              }`}
                            />
                          </div>
                        </div>
                        <div className="col-xl-12 col-sm-12">
                          <div className="form-group">
                            <label htmlFor="message" className="form-label">
                              Message <span style={{ color: "red" }}>*</span>
                            </label>
                            <textarea
                              type="text"
                              rows="4"
                              className={`form-control ${
                                formik.errors.message && formik.touched.message
                                  ? "validation-border"
                                  : ""
                              }`}
                              name="message"
                              value={formik.values.message}
                              onChange={(message) =>
                                formik.setFieldValue(
                                  "message",
                                  message.target.value,
                                )
                              }
                              placeholder="Short message"
                            />
                          </div>
                        </div>
                        <div className="col-xl-12 col-sm-12">
                          <Button className="primary-btn w-100" type="submit">
                            {loading ? <Spinner size="sm" /> : "SUBMIT DETAILS"}
                          </Button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* OUR PEOPLE */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="our-people"
      >
        <div className="container">
          <h2>OUR TEAM</h2>
          <div className="row justify-content-center">
            <div className="col-xl-10 col-sm-12">
              <p>
                We honor professionalism, hard work, individual attention, and
                pride ourselves on conducting solid research. Our depth of
                experience and commitment to our values combine to bring you the
                best possible service.
              </p>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-xl-4 col-lg-6 col-sm-12">
              <div className="people-section">
                <img src={MichealAvendano} alt="Michael Avendano" />
                <h3>Michael Avendano</h3>
                <p className="designation">Managing Broker</p>
                <p className="bio">
                  Meet Michael Avendano, Our Managing Broker. He has transformed
                  our office into a productive environment. He is a great
                  example of hard work and dedication. He is professional,
                  diligent and always supportive. He is also a real estate
                  investor and owns a property management company. He is happily
                  married with 3 beautiful kids, always keeping a healthy
                  balance between family and work.
                </p>
              </div>
            </div>
            <div className="col-xl-4 col-lg-6 col-sm-12">
              <div className="people-section">
                <img src={AmiKnox} alt="Ami Knox" />
                <h3>Ami Knox</h3>
                <p className="designation">Director of Operations</p>
                <p className="bio">
                  Meet Ami Knox, Our Director Of Operations. With 20+ years in
                  the real estate industry, she is a great asset to our firm.
                  Ami is honest, diligent, outgoing and cares deeply about
                  people. She also has a keen eye for design as she created the
                  look and feel of our industrial modern office. Stop by and say
                  hello to Ami Knox.
                </p>
              </div>
            </div>
            <div className="col-xl-4 col-lg-6 col-sm-12">
              <div className="people-section">
                <img src={JamesSmith} alt="Benny Chavez" />
                <h3>Benny Chavez</h3>
                <p className="designation">Agent Advisor</p>
                <p className="bio">
                  Benny Chavez discovered the benefits of hard work and the joy
                  of customer satisfaction early in life, working at his
                  father’s auto repair shop at age fourteen. At age nineteen, he
                  saved enough money to buy ten acres of real estate in Joshua
                  Tree. Though this investment did not result in monetary gain,
                  it sparked an interest in real estate that has blossomed into
                  a successful career.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* OUR OFFICES */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        className="our-offices"
      >
        <div className="container">
          <h2>TOUR DE AMBERWOOD</h2>
          <div className="row mt-3 justify-content-center">
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-01.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-02.png"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-05.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-03.png"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-02.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/hiring-04.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src="https://storage.googleapis.com/cdn-amberwoodre/office/office-06.webp"
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-sm-12">
              <div className="office-image">
                <img
                  src={require("../../assets/selected/office-new-03.png")}
                  alt="11060 Artesia Blvd Suite G, Cerritos, CA, USA"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactUs;
